import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { MapContainer, TileLayer, Marker, useMapEvents } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

const icon = new L.Icon({
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  iconSize: [25, 41]
});

const fetchLiveAddress = async (pos, setLiveAddress) => {
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&addressdetails=1&lat=${pos[0]}&lon=${pos[1]}`
    );
    const data = await res.json();
    if (setLiveAddress) setLiveAddress(data.display_name || "Custom location selected");
  } catch (err) {
    if (setLiveAddress) setLiveAddress("Custom location selected");
  }
};

function DraggableMarker({ position, setMapPosition, setLiveAddress }) {
  const markerRef = React.useRef(null);

  const eventHandlers = React.useMemo(
    () => ({
      async dragend() {
        const marker = markerRef.current;
        if (marker != null) {
          const latLng = marker.getLatLng();
          const newPos = [latLng.lat, latLng.lng];
          setMapPosition(newPos);
          fetchLiveAddress(newPos, setLiveAddress);
        }
      },
    }),
    [setMapPosition, setLiveAddress]
  );

  return (
    <Marker
      draggable={true}
      eventHandlers={eventHandlers}
      position={position}
      ref={markerRef}
      icon={icon}
    />
  );
}

function LocationMarker({ setMapPosition, setLiveAddress }) {
  useMapEvents({
    click(e) {
      const newPos = [e.latlng.lat, e.latlng.lng];
      setMapPosition(newPos);
      fetchLiveAddress(newPos, setLiveAddress);
    }
  });
  return null;
}

function FlyMap({ position }) {
  const map = useMapEvents({});

  React.useEffect(() => {
    if (position) {
      map.flyTo(position, 17); // ⭐ High precision zoom (building level)
    }
  }, [position]);

  return null;
}
export default function Cart() {

  const { cart, updateCartItem, removeFromCart, cartTotal, clearCart } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [paymentStep, setPaymentStep] = useState(false);
  const [placing, setPlacing] = useState(false);
  const [checkoutStep, setCheckoutStep] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [shipping, setShipping] = useState({
    fullName: '',
    street: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'India',
    latitude: null,
    longitude: null
  });

  const [paymentMethod, setPaymentMethod] = useState('cod');

  // ⭐ MAP STATES
  const [showMap, setShowMap] = useState(false);
  const [mapPosition, setMapPosition] = useState([13.0827, 80.2707]);
  const [isLocating, setIsLocating] = useState(false);
  const [geoAccuracy, setGeoAccuracy] = useState(null);
  const [liveAddress, setLiveAddress] = useState('');

  // ⭐ SAVED ADDRESS BOOK STATES
  const [savedAddresses, setSavedAddresses] = useState([]);
  const [selectedAddressId, setSelectedAddressId] = useState(null);
  const [saveToProfile, setSaveToProfile] = useState(false);

  React.useEffect(() => {
    if (user) {
      api.get('/auth/addresses')
        .then(res => {
          if (res.data.success && res.data.addresses?.length > 0) {
            setSavedAddresses(res.data.addresses);
            const defaultAddr = res.data.addresses.find(a => a.isDefault) || res.data.addresses[0];
            if (defaultAddr) {
              selectSavedAddress(defaultAddr);
            }
          }
        })
        .catch(err => console.error("Error loading saved addresses:", err));
    }
  }, [user]);

  const selectSavedAddress = (addr) => {
    setSelectedAddressId(addr._id);
    setShipping({
      fullName: addr.fullName || `${user?.firstName || ''} ${user?.lastName || ''}`.trim(),
      street: addr.street || '',
      address: addr.address || '',
      city: addr.city || '',
      state: addr.state || '',
      zipCode: addr.zipCode || '',
      country: addr.country || 'India',
      latitude: addr.latitude,
      longitude: addr.longitude
    });
    if (addr.latitude && addr.longitude) {
      setMapPosition([addr.latitude, addr.longitude]);
    }
  };

  const formatINR = (price) =>
    new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(price);
  const items = cart?.products || [];
  const [typingTimeout, setTypingTimeout] = useState(null);

  const searchAddress = (value) => {

    setSearchQuery(value);

    if (typingTimeout) clearTimeout(typingTimeout);

    const timeout = setTimeout(async () => {

      if (value.length < 3) {
        setSuggestions([]);
        return;
      }

      try {

        const res = await fetch(
          `https://nominatim.openstreetmap.org/search?format=json&limit=5&countrycodes=in&q=${value}`,
          {
            headers: {
              "Accept": "application/json"
            }
          }
        );

        const data = await res.json();
        setSuggestions(data);

      } catch (err) {
        console.log("Nominatim blocked request");
        setSuggestions([]);
      }

    }, 500);   // ⭐ delay

    setTypingTimeout(timeout);

  };
  const selectSuggestion = async (place) => {

    const lat = parseFloat(place.lat);
    const lng = parseFloat(place.lon);

    setMapPosition([lat, lng]);
    setSuggestions([]);
    setSearchQuery(place.display_name);

    const res = await fetch(
      `https://nominatim.openstreetmap.org/reverse?format=json&addressdetails=1&lat=${lat}&lon=${lng}`,
      {
        headers: {
          "Accept": "application/json"
        }
      }
    );

    const data = await res.json();
    const addr = data.address;

    setShipping(prev => ({
      ...prev,
      address:
        `${addr.house_number || ""} ${addr.road || ""} ${addr.suburb || addr.neighbourhood || ""}`,

      city: addr.city || addr.town || addr.village || "",
      state: addr.state || "",
      zipCode: addr.postcode || "",
      latitude: lat,          // ✅ ADD
      longitude: lng
    }));

  };
  const handlePlaceOrder = async (e) => {
    e.preventDefault();

    try {
      if (saveToProfile && user) {
        api.post('/auth/addresses', {
          ...shipping,
          label: 'Home'
        }).catch(err => console.error("Could not save address to profile:", err));
      }

      const formattedProducts = items.map(item => ({
        product: item.product._id,
        name: item.product.name,
        brand: item.product.brand,
        image: item.isCustom ? item.customThumbnail : item.product.images[0],
        price: item.product.finalPrice || item.product.price,
        quantity: item.quantity,
        size: item.size,
        color: item.color,
        isCustom: item.isCustom || false,
        design: item.design || null,
        customThumbnail: item.customThumbnail || ''
      }));
      if (paymentMethod === "cod") {
        await api.post("/orders", {
          shippingAddress: shipping,
          products: formattedProducts,
          totalAmount: grandTotal,
          paymentMethod: "cod"
        });
        await clearCart();
        alert("Order placed");
        navigate("/");
        return;
      }

      console.log("STEP 1: Creating Razorpay order");

      const { data } = await api.post("/payment/create-order", {
        amount: grandTotal
      });

      console.log("STEP 2: Order created", data);

      if (!data || !data.order) {
        alert("Order creation failed");
        return;
      }

      const options = {
        key: "rzp_live_Sdf6dZAMrPkbov", // ✅ PUT YOUR REAL KEY
        amount: data.order.amount,
        currency: "INR",
        name: "AURÉNE",
        description: "Order Payment",
        order_id: data.order.id,

        handler: async function (response) {
          console.log("STEP 3: Payment success", response);

          await api.post("/orders", {
            userId: user?._id,
            shippingAddress: shipping,
            products: formattedProducts,
            totalAmount: grandTotal,
            paymentMethod: "online",
            paymentId: response.razorpay_payment_id
          });
          await clearCart();
          alert("Payment successful 🎉");
          navigate("/");
        }
      };

      console.log("STEP 4: Opening Razorpay");

      const rzp = new window.Razorpay(options);
      rzp.open();

    } catch (err) {
      console.error("PAYMENT ERROR FULL:", err);
      console.error("RESPONSE:", err.response);
      console.error("DATA:", err.response?.data);

      alert(JSON.stringify(err.response?.data || "Error"));

    }
  };
  const handleOnlinePayment = async () => {
    try {
      const res = await api.post("/orders/create-payment", {
        amount: total
      });

      const order = res.data;

      const options = {
        key: "rzp_test_xxxxx",
        amount: data.order.amount,
        currency: "INR",
        name: "AURÉNE",
        description: "Order Payment",
        order_id: data.order.id,   // ✅ FIXED

        handler: async function (response) {
          await api.post("/orders", {
            userId: user?._id,   // 🔥 ADD THIS
            shippingAddress: shipping,
            products: formattedProducts,
            totalAmount: grandTotal,
            paymentMethod: "online",
            paymentId: response.razorpay_payment_id
          });


          alert("Payment Successful 🎉");
        },

        theme: {
          color: "#000"
        }
      };

      const rzp = new window.Razorpay(options);
      rzp.open();

    } catch (err) {
      console.log(err);
    }
  };
  const detectLocation = () => {
    if (!navigator.geolocation) {
      alert("Geolocation is not supported by your browser");
      return;
    }

    setIsLocating(true);

    const geoOptions = {
      enableHighAccuracy: true, // Forces hardware GPS & high-accuracy WiFi triangulation
      timeout: 12000,           // Allow up to 12s for high-accuracy GPS lock
      maximumAge: 0             // Always request fresh position
    };

    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        setIsLocating(false);
        const lat = pos.coords.latitude;
        const lng = pos.coords.longitude;
        const accuracy = Math.round(pos.coords.accuracy);

        setGeoAccuracy(accuracy);
        setMapPosition([lat, lng]);

        try {
          const res = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&addressdetails=1&lat=${lat}&lon=${lng}`
          );
          const data = await res.json();
          const addr = data.address || {};

          setShipping(prev => ({
            ...prev,
            address: `${addr.house_number || ""} ${addr.road || ""} ${addr.suburb || addr.neighbourhood || ""}`.trim() || data.display_name || "",
            city: addr.city || addr.town || addr.village || "",
            state: addr.state || "",
            zipCode: addr.postcode || "",
            latitude: lat,
            longitude: lng
          }));

          if (accuracy > 150) {
            alert(`Location detected with ~${accuracy}m accuracy (IP/WiFi network range). Feel free to adjust the marker on the map for your exact doorstep.`);
          }
        } catch (err) {
          console.error("Reverse geocoding error:", err);
        }
      },
      (err) => {
        setIsLocating(false);
        let msg = "Could not fetch location.";
        if (err.code === 1) msg = "Location permission denied. Please allow location access in your browser.";
        else if (err.code === 2) msg = "Location unavailable. Please check your GPS/Network.";
        else if (err.code === 3) msg = "Location request timed out. Please try again or pick on map.";
        alert(msg);
      },
      geoOptions
    );
  };

  const shippingCost = cartTotal >= 1500 ? 0 : 0;
  const grandTotal = cartTotal + shippingCost;

  return (
    <div className="min-h-screen px-6 py-10">

      <div className="grid lg:grid-cols-3 gap-10">

        {/* ITEMS */}
        <div className="lg:col-span-2">
          {items.map(item => (
            <div key={item._id} className="flex gap-6 border-b py-8 hover:bg-lux-bg/30 transition-colors px-4">
              <div className="relative w-24 h-32 flex-shrink-0 group">
                <img
                  src={item.isCustom && item.customThumbnail ? item.customThumbnail : (item.product?.images?.[0] || 'https://images.unsplash.com/photo-1558618666-fcd25c85cd64?w=200&q=80')}
                  className="w-full h-full object-cover border border-lux-border"
                  alt={item.product?.name}
                />
                {item.isCustom && (
                  <div className="absolute top-0 left-0 bg-lux-gold text-white text-[8px] px-2 py-0.5 uppercase font-bold tracking-tighter">
                    Custom Design
                  </div>
                )}
              </div>

              <div className="flex-1">
                <div className="flex justify-between items-start">
                  <div>
                    <p className="text-[10px] uppercase tracking-widest text-lux-gold font-bold mb-1">
                      {item.product?.brand}
                    </p>
                    <h3 className="font-display text-xl text-lux-primary">
                      {item.isCustom ? 'Designed Shirt' : item.product?.name}
                    </h3>
                    <p className="text-[10px] text-lux-secondary mt-1 uppercase tracking-tighter">
                      Size: {item.size || 'N/A'} · Color: {item.color || 'N/A'}
                    </p>
                  </div>
                  <p className="font-bold text-lux-primary">
                    {formatINR((item.product?.finalPrice || item.product?.price) * item.quantity)}
                  </p>
                </div>

                <div className="flex justify-between mt-6">
                  <div className="flex border">
                    <button onClick={() => updateCartItem(item._id, item.quantity - 1)}>−</button>
                    <span className="px-3">{item.quantity}</span>
                    <button onClick={() => updateCartItem(item._id, item.quantity + 1)}>+</button>
                  </div>

                  <button onClick={() => removeFromCart(item._id)}>
                    Remove
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* SUMMARY */}
        <div className="border p-6 h-fit">

          <h2 className="text-xl mb-6">Summary</h2>

          <div className="flex justify-between">
            <span>Subtotal</span>
            <span>{formatINR(cartTotal)}</span>
          </div>

          <div className="flex justify-between">
            <span>Shipping</span>
            <span>{formatINR(shippingCost)}</span>
          </div>

          <div className="flex justify-between font-bold mt-4">
            <span>Total</span>
            <span>{formatINR(grandTotal)}</span>
          </div>

          {!checkoutStep ? (
            <button
              onClick={() => setCheckoutStep(true)}
              className="w-full bg-black text-white mt-6 py-3"
            >
              Proceed Checkout
            </button>
          ) : (

            <form onSubmit={handlePlaceOrder} className="space-y-4 mt-6">

              {/* ⭐ SAVED ADDRESSES SELECTOR */}
              {savedAddresses.length > 0 && (
                <div className="space-y-2 mb-4 border-b pb-4">
                  <span className="text-[10px] uppercase font-bold tracking-widest text-lux-gold block">
                    ⚡ 1-Click Saved Address Selector
                  </span>
                  <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                    {savedAddresses.map((addr) => (
                      <div
                        key={addr._id}
                        onClick={() => selectSavedAddress(addr)}
                        className={`p-2.5 rounded border text-xs cursor-pointer transition-all flex justify-between items-start ${
                          selectedAddressId === addr._id
                            ? 'border-lux-gold bg-lux-gold/10 font-medium'
                            : 'border-gray-200 hover:border-gray-400 bg-white'
                        }`}
                      >
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-black text-white">
                              {addr.label === 'Home' && '🏠 Home'}
                              {addr.label === 'Work' && '🏢 Work'}
                              {addr.label === 'Other' && `📍 ${addr.customLabel || 'Other'}`}
                            </span>
                            <span className="font-bold text-lux-primary">{addr.fullName}</span>
                          </div>
                          <p className="text-[11px] text-lux-secondary mt-1 line-clamp-1">{addr.address}, {addr.city}</p>
                        </div>
                        {addr.latitude && addr.longitude && (
                          <span className="text-[9px] font-mono text-lux-gold font-bold flex-shrink-0 ml-2">
                            🎯 Pinned
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <input
                placeholder="Full Name"
                value={shipping.fullName}
                onChange={e => setShipping({ ...shipping, fullName: e.target.value })}
                required
                className="w-full border-b py-2"
              />

              <input
                placeholder="Flat / Door No / Landmark"
                value={shipping.address}
                onChange={(e) =>
                  setShipping({ ...shipping, address: e.target.value })
                }
                required
                className="w-full border-b py-2"
              />

              {/* AREA / STREET + MAP BUTTON */}
              <div className="flex gap-2">
                <input
                  placeholder="Area / Street"
                  value={shipping.street}
                  onChange={e => setShipping({ ...shipping, street: e.target.value })}
                  className="flex-1 border-b py-2"
                />

                <button
                  type="button"
                  onClick={() => setShowMap(true)}
                  className="border border-black px-3 text-[10px] uppercase font-bold tracking-wider hover:bg-black hover:text-white transition-colors flex items-center gap-1 whitespace-nowrap"
                >
                  📍 Pick on Map
                </button>
              </div>

              <input
                placeholder="City"
                value={shipping.city}
                onChange={e => setShipping({ ...shipping, city: e.target.value })}
                required
                className="w-full border-b py-2"
              />

              <input
                placeholder="State"
                value={shipping.state}
                onChange={e => setShipping({ ...shipping, state: e.target.value })}
                required
                className="w-full border-b py-2"
              />

              <input
                placeholder="PIN Code"
                value={shipping.zipCode}
                onChange={e => setShipping({ ...shipping, zipCode: e.target.value })}
                required
                className="w-full border-b py-2"
              />

              {user && (
                <label className="flex items-center gap-2 cursor-pointer text-xs pt-1 pb-2">
                  <input
                    type="checkbox"
                    checked={saveToProfile}
                    onChange={e => setSaveToProfile(e.target.checked)}
                    className="accent-black"
                  />
                  <span className="text-[11px] text-lux-secondary">Save this address to my profile for future orders</span>
                </label>
              )}

              {!paymentStep ? (
                <button
                  type="button"
                  onClick={() => setPaymentStep(true)}
                  className="w-full bg-black text-white py-3 font-sans text-xs uppercase tracking-widest hover:bg-lux-gold transition-colors font-semibold"
                >
                  Continue to Payment
                </button>
              ) : (
                <>
                  <div className="border p-4 space-y-3">

                    <h3 className="font-semibold">Select Payment Method</h3>

                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="payment"
                        value="cod"
                        checked={paymentMethod === "cod"}
                        onChange={() => setPaymentMethod("cod")}
                      />
                      Cash on Delivery
                    </label>

                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="radio"
                        name="payment"
                        value="online"
                        checked={paymentMethod === "online"}
                        onChange={() => setPaymentMethod("online")}
                      />
                      Online Payment
                    </label>

                  </div>

                  <button
                    disabled={placing}
                    className="w-full bg-black text-white py-3 mt-3"
                  >
                    {placing ? "Processing..." : "Place Order"}
                  </button>
                </>
              )}

            </form>
          )}
        </div>
      </div>

      {/* ⭐ MAP MODAL */}
      {showMap && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex justify-center items-center z-50 p-4">
          <div className="bg-white p-6 rounded-md shadow-2xl w-full max-w-2xl space-y-4 border border-lux-border">
            
            <div className="flex justify-between items-center border-b pb-3">
              <div>
                <h3 className="font-display text-xl text-lux-primary">Pin Exact Delivery Location</h3>
                <p className="text-[11px] text-lux-secondary">Drag marker or click on map to pinpoint doorstep</p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={detectLocation}
                  disabled={isLocating}
                  className="border border-black px-3 py-1.5 text-[10px] uppercase tracking-wider font-bold hover:bg-black hover:text-white transition-colors disabled:opacity-50 flex items-center gap-1 bg-lux-bg/50"
                  title="Detect GPS Hardware Location"
                >
                  {isLocating ? <span className="animate-spin">⌛ Locating...</span> : <span>📍 Detect My Location</span>}
                </button>
                <button 
                  onClick={() => setShowMap(false)}
                  className="text-lux-secondary hover:text-lux-primary font-bold text-lg px-2"
                >
                  ✕
                </button>
              </div>
            </div>

            <div className="relative overflow-hidden rounded border border-lux-border">
              <MapContainer center={mapPosition} zoom={17} style={{ height: 380, width: '100%' }}>
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                <FlyMap position={mapPosition} />
                <DraggableMarker position={mapPosition} setMapPosition={setMapPosition} setLiveAddress={setLiveAddress} />
                <LocationMarker setMapPosition={setMapPosition} setLiveAddress={setLiveAddress} />
              </MapContainer>
            </div>

            {liveAddress && (
              <div className="bg-lux-bg/80 border border-lux-gold/40 p-3 text-xs font-sans text-lux-primary rounded flex justify-between items-start">
                <div>
                  <span className="font-bold text-lux-gold uppercase tracking-wider block mb-0.5">📍 Pinned Address Preview:</span>
                  <span className="line-clamp-2 leading-relaxed">{liveAddress}</span>
                </div>
                {geoAccuracy && (
                  <span className="text-[10px] text-lux-gold font-mono font-bold whitespace-nowrap ml-2">
                    🎯 ±{geoAccuracy}m
                  </span>
                )}
              </div>
            )}

            <div className="flex justify-between items-center pt-2 border-t border-lux-border">
              <p className="text-[11px] text-lux-secondary font-mono">
                {mapPosition[0].toFixed(5)}° N, {mapPosition[1].toFixed(5)}° E
              </p>
              
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => setShowMap(false)}
                  className="px-4 py-2 border text-xs uppercase tracking-wider font-sans hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      const res = await fetch(
                        `https://nominatim.openstreetmap.org/reverse?format=json&addressdetails=1&lat=${mapPosition[0]}&lon=${mapPosition[1]}`
                      );
                      const data = await res.json();
                      const addr = data.address || {};

                      setShipping(prev => ({
                        ...prev,
                        address: `${addr.house_number || ""} ${addr.road || ""} ${addr.suburb || addr.neighbourhood || ""}`.trim() || data.display_name || "",
                        city: addr.city || addr.town || addr.village || prev.city || "",
                        state: addr.state || prev.state || "",
                        zipCode: addr.postcode || prev.zipCode || "",
                        latitude: mapPosition[0],
                        longitude: mapPosition[1]
                      }));
                    } catch (err) {
                      console.error("Address fetch error:", err);
                    }
                    setShowMap(false);
                  }}
                  className="bg-black text-white px-6 py-2 text-xs uppercase tracking-wider font-sans hover:bg-lux-gold transition-colors font-semibold"
                >
                  Confirm Pinned Location
                </button>
              </div>
            </div>

          </div>
        </div>
      )}

    </div>
  );
}